import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import MiniHeader from '../../../components/layout/MiniHeader/index'
import Button from '../../../components/common/Button/index'
import Typography from '../../../components/common/Typography/index'
import Card from '../../../components/common/Card/index'
import LoadingIndicator from '../../../components/state/LoadingIndicator'
import ErrorState from '../../../components/state/ErrorState'
import useAppSelector from '../../../state/redux/hooks/index'
import {
  getAllContactMessages,
  getContactMessageDetails,
  updateContactMessageStatus,
} from '../../../state/redux/actions/helpAction'
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLinkedin,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaEye,
  FaCheck,
  FaClock,
  FaExclamationTriangle,
} from 'react-icons/fa'
import api from '../../../shared/utils/api'

const ContactUs = () => {
  const dispatch = useDispatch()

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    subject: 'General Inquiry',
    message: '',
  })

  // UI state
  const [activeTab, setActiveTab] = useState('form')
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState(null)
  const [formSuccess, setFormSuccess] = useState(false)

  // Redux state - Updated to match your slice structure
  const { allContactMessages, contactMessageDetails, loading, error } = useAppSelector(state => state.help)

  // Load contact messages on component mount and when filters change
  useEffect(() => {
    if (activeTab === 'messages') {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {}
      dispatch(getAllContactMessages(params))
    }
  }, [dispatch, activeTab, filterStatus])

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))

    // Clear form error when user starts typing
    if (formError) {
      setFormError(null)
    }

    // Clear success message when user starts editing
    if (formSuccess) {
      setFormSuccess(false)
    }
  }

  const validateForm = () => {
    const { firstName, lastName, email, message } = formData

    if (!firstName.trim()) {
      setFormError('First name is required')
      return false
    }

    if (!lastName.trim()) {
      setFormError('Last name is required')
      return false
    }

    if (!email.trim()) {
      setFormError('Email is required')
      return false
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setFormError('Please enter a valid email address')
      return false
    }

    if (!message.trim()) {
      setFormError('Message is required')
      return false
    }

    return true
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setFormLoading(true)
    setFormError(null)

    try {
      // Submit contact form via API
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        subject: formData.subject,
        message: formData.message.trim(),
        status: 'pending',
        createdAt: new Date().toISOString(),
      }

      const response = await api.post('/v1/help/contact-messages', payload)

      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        subject: 'General Inquiry',
        message: '',
      })

      setFormSuccess(true)

      // If we're on the messages tab, refresh the list
      if (activeTab === 'messages') {
        const params = filterStatus !== 'all' ? { status: filterStatus } : {}
        dispatch(getAllContactMessages(params))
      }

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setFormSuccess(false)
      }, 5000)
    } catch (error) {
      console.error('Error submitting form:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Error sending message. Please try again.'
      setFormError(errorMessage)
    } finally {
      setFormLoading(false)
    }
  }

  const handleViewMessage = async messageId => {
    try {
      await dispatch(getContactMessageDetails(messageId))
      setSelectedMessage(messageId)
      setShowMessageModal(true)
    } catch (error) {
      console.error('Error fetching message details:', error)
    }
  }

  const handleStatusUpdate = async (messageId, newStatus, notes = '') => {
    try {
      await dispatch(
        updateContactMessageStatus(messageId, {
          status: newStatus,
          notes: notes,
          updatedAt: new Date().toISOString(),
        })
      )

      // Refresh messages list
      const params = filterStatus !== 'all' ? { status: filterStatus } : {}
      dispatch(getAllContactMessages(params))

      // Close modal if we're updating the currently viewed message
      if (selectedMessage === messageId) {
        setShowMessageModal(false)
        setSelectedMessage(null)
      }
    } catch (error) {
      console.error('Error updating message status:', error)
    }
  }

  const getStatusIcon = status => {
    switch (status) {
      case 'pending':
        return <FaClock className="text-teal-600" />
      case 'resolved':
        return <FaCheck className="text-teal-700" />
      case 'in_progress':
        return <FaExclamationTriangle className="text-teal-500" />
      default:
        return <FaClock className="text-teal-400" />
    }
  }

  const getStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'bg-teal-100 text-teal-800'
      case 'resolved':
        return 'bg-teal-200 text-teal-900'
      case 'in_progress':
        return 'bg-teal-50 text-teal-700'
      default:
        return 'bg-teal-50 text-teal-600'
    }
  }

  // Get filtered messages from Redux state - Updated to match your slice structure
  const getFilteredMessages = () => {
    // Handle different possible data structures from your slice
    let messages = []

    if (allContactMessages) {
      if (Array.isArray(allContactMessages)) {
        messages = allContactMessages
      } else if (allContactMessages.data && Array.isArray(allContactMessages.data)) {
        messages = allContactMessages.data
      } else if (allContactMessages.messages && Array.isArray(allContactMessages.messages)) {
        messages = allContactMessages.messages
      }
    }

    if (filterStatus === 'all') {
      return messages
    }

    return messages.filter(message => message.status === filterStatus)
  }

  const filteredMessages = getFilteredMessages()

  // Get total count for display
  const getTotalCount = () => {
    if (allContactMessages) {
      if (allContactMessages.total) {
        return allContactMessages.total
      } else if (Array.isArray(allContactMessages)) {
        return allContactMessages.length
      } else if (allContactMessages.data && Array.isArray(allContactMessages.data)) {
        return allContactMessages.data.length
      }
    }
    return 0
  }

  return (
    <>
      <MiniHeader title="Contact Us" />
      <div className="ml-[250px] p-8 max-w-7xl mx-auto">
        {/* Tab Navigation */}
        <div className="flex gap-1 p-1 bg-primary rounded-xl mb-6 w-fit">
          <Button
            size="medium"
            variant={activeTab === 'form' ? 'secondary' : null}
            className={
              activeTab === 'form'
                ? 'bg-white text-gray-800 rounded-lg px-6 py-2 font-medium shadow-sm'
                : 'text-white px-6 py-2 font-medium hover:bg-white/10 rounded-lg transition-colors'
            }
            onClick={() => setActiveTab('form')}
          >
            Contact Form
          </Button>
          <Button
            size="medium"
            variant={activeTab === 'messages' ? 'secondary' : null}
            className={
              activeTab === 'messages'
                ? 'bg-white text-gray-800 rounded-lg px-6 py-2 font-medium shadow-sm'
                : 'text-white px-6 py-2 font-medium hover:bg-white/10 rounded-lg transition-colors'
            }
            onClick={() => setActiveTab('messages')}
          >
            Manage Messages ({getTotalCount()})
          </Button>
        </div>

        {/* Global Loading and Error States */}
        {loading && <LoadingIndicator />}
        {error && <ErrorState error={error} />}

        {/* Contact Form Tab */}
        {activeTab === 'form' && (
          <div className="">
            <div className="flex justify-center items-center flex-col text-center py-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Contact Us</h2>
              <p className="text-gray-600 max-w-md">Any question or remarks? Just write us a message!</p>
            </div>

            {/* Success Message */}
            {formSuccess && (
              <div className="mb-6 p-4 bg-teal-100 border border-teal-400 text-teal-700 rounded-md">
                <div className="flex items-center">
                  <FaCheck className="mr-2" />
                  <span>Message sent successfully! We'll get back to you soon.</span>
                </div>
              </div>
            )}

            {/* Error Message */}
            {formError && (
              <div className="mb-6 p-4 bg-teal-50 border border-teal-300 text-teal-800 rounded-md">
                <div className="flex items-center">
                  <FaExclamationTriangle className="mr-2" />
                  <span>{formError}</span>
                </div>
              </div>
            )}

            {/* Contact Form and Information */}
            <div className="px-6 pb-8">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-12 gap-0 bg-white rounded-lg overflow-hidden shadow-lg">
                  <div className="col-span-3 bg-teal-500 text-white p-8 relative">
                    <div className="relative z-10">
                      <Typography variant="h3" className="text-xl font-semibold mb-2 text-white">
                        Contact Information
                      </Typography>
                      <Typography className="text-teal-100 mb-8 text-sm">
                        Say something to start a live chat!
                      </Typography>

                      <div className="space-y-6">
                        <div className="flex items-center space-x-4">
                          <FaPhone className="text-white text-lg" />
                          <Typography className="text-white">+1012 3456 789</Typography>
                        </div>

                        <div className="flex items-center space-x-4">
                          <FaEnvelope className="text-white text-lg" />
                          <Typography className="text-white">demo@gmail.com</Typography>
                        </div>

                        <div className="flex items-start space-x-4">
                          <FaMapMarkerAlt className="text-white text-lg mt-1" />
                          <div>
                            <Typography className="text-white">132 Dartmouth Street Boston,</Typography>
                            <Typography className="text-white">Manchester - Dubai</Typography>
                          </div>
                        </div>
                      </div>

                      {/* Social Media */}
                      <div className="mt-12">
                        <Typography className="text-white mb-4 text-sm">Follow us on</Typography>
                        <div className="flex space-x-4">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-800">
                            <FaLinkedin className="text-black text-sm" />
                          </div>
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-800">
                            <FaFacebook className="text-black text-sm" />
                          </div>
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-800">
                            <FaTwitter className="text-black text-sm" />
                          </div>
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-800">
                            <FaInstagram className="text-black text-sm" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Decorative circles */}
                    <div className="absolute bottom-8 right-8 w-32 h-32 bg-teal-400 rounded-full opacity-20"></div>
                    <div className="absolute bottom-16 right-16 w-16 h-16 bg-teal-300 rounded-full opacity-30"></div>
                  </div>

                  {/* Contact Form Side */}
                  <div className="col-span-9 p-8">
                    <div className="space-y-6">
                      {/* Name Fields */}
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm text-gray-600 mb-2">First Name *</label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="w-full border-b border-gray-300 pb-2 focus:border-teal-500 focus:outline-none transition-colors"
                            required
                            disabled={formLoading}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-2">Last Name *</label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="w-full border-b border-gray-300 pb-2 focus:border-teal-500 focus:outline-none transition-colors"
                            required
                            disabled={formLoading}
                          />
                        </div>
                      </div>

                      {/* Email and Phone */}
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm text-gray-600 mb-2">Email *</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full border-b border-gray-300 pb-2 focus:border-teal-500 focus:outline-none transition-colors"
                            required
                            disabled={formLoading}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-2">Phone Number</label>
                          <input
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            placeholder="+1 012 3456 789"
                            className="w-full border-b border-gray-300 pb-2 focus:border-teal-500 focus:outline-none transition-colors"
                            disabled={formLoading}
                          />
                        </div>
                      </div>

                      {/* Subject Selection */}
                      <div>
                        <label className="block text-sm text-gray-600 mb-4">Select Subject? *</label>
                        <div className="flex gap-4">
                          {['General Inquiry', 'Technical Support', 'Billing Question', 'Feature Request'].map(
                            (subject, index) => (
                              <label key={index} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="subject"
                                  value={subject}
                                  checked={formData.subject === subject}
                                  onChange={handleInputChange}
                                  className="text-teal-500 focus:ring-teal-500"
                                  disabled={formLoading}
                                />
                                <span className="text-sm text-gray-700">{subject}</span>
                              </label>
                            )
                          )}
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-sm text-gray-600 mb-2">Message *</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Write your message.."
                          rows={1}
                          className="w-full border-b border-gray-300 pb-2 focus:border-teal-500 focus:outline-none transition-colors resize-none"
                          required
                          disabled={formLoading}
                        ></textarea>
                      </div>

                      <div className="flex justify-end pt-4">
                        <Button
                          type="submit"
                          className="px-8 py-3 bg-teal-500 text-white hover:bg-teal-600 transition-colors rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={formLoading}
                        >
                          {formLoading ? 'Sending...' : 'Send Message'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Messages Management Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-6">
            {/* Filter Controls */}
            <div className="flex justify-between items-center">
              <Typography variant="h4" className="text-gray-800 font-semibold">
                Contact Messages ({filteredMessages.length})
              </Typography>

              <div className="flex items-center gap-4">
                <Typography variant="body" className="text-gray-600">
                  Filter by status:
                </Typography>
                <select
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={loading}
                >
                  <option value="all">All Messages</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
            </div>

            {/* Messages List */}
            <div className="grid gap-4">
              {filteredMessages.length > 0 ? (
                filteredMessages.map(message => (
                  <Card
                    key={message._id}
                    className="p-6 border border-gray-200 rounded-xl hover:shadow-lg transition-all duration-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Typography variant="h6" className="font-semibold text-gray-900">
                            {message.fullName ||
                              `${message.firstName || ''} ${message.lastName || ''}`.trim() ||
                              'Anonymous'}
                          </Typography>
                          <div
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${getStatusColor(
                              message.status
                            )}`}
                          >
                            {getStatusIcon(message.status)}
                            <span className="capitalize">{message.status?.replace('_', ' ')}</span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 mb-4">
                          <span className="font-medium text-gray-700">Email:</span> {message.email || '-'}
                        </div>
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-2 ml-4">
                        <Button
                          size="small"
                          onClick={() => handleViewMessage(message._id)}
                          className="px-3 py-1 bg-teal-500 text-white hover:bg-teal-600 transition-colors rounded-lg flex items-center gap-1.5"
                          disabled={loading}
                        >
                          <FaEye className="text-xs" />
                          View
                        </Button>
                        {message.status !== 'resolved' && (
                          <Button
                            size="small"
                            onClick={() => handleStatusUpdate(message._id, 'resolved')}
                            className="px-3 py-1 bg-teal-500 text-white hover:bg-teal-600 transition-colors rounded-lg flex items-center gap-1.5"
                            disabled={loading}
                          >
                            <FaCheck className="text-xs" />
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>

                    <hr className="my-4 border-gray-200" />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <Typography variant="body" className="text-gray-500 text-xs">
                          Category
                        </Typography>
                        <Typography variant="h5" className="font-small font-semibold text-gray-800">
                          {message.category || '-'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="body" className="text-gray-500 text-xs">
                          Source
                        </Typography>
                       <Typography variant="h5" className="font-small font-semibold text-gray-800">
                          {message.source || '-'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="body" className="text-gray-500 text-xs">
                          Received
                        </Typography>
                        <Typography variant="h5" className="font-small text-gray-800">
                          {message.createdAt
                            ? new Date(message.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })
                            : '-'}
                        </Typography>
                      </div>
                    </div>

                    <Typography variant="body" className="text-gray-700 mb-2 bg-gray-50 p-3 rounded line-clamp-2">
                      {message.description || message.message || message.body || '-'}
                    </Typography>

                    <Typography variant="body" className="text-gray-500 text-xs">
                      Last updated:{' '}
                      {message.updatedAt
                        ? new Date(message.updatedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-'}
                    </Typography>
                  </Card>
                ))
              ) : (
                <Card className="p-8 text-center bg-gray-50">
                  <Typography variant="body" className="text-gray-500">
                    {loading ? 'Loading messages...' : 'No contact messages found.'}
                  </Typography>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Message Details Modal */}
        {showMessageModal && selectedMessage && contactMessageDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <Card className="max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <Typography variant="h5" className="font-semibold text-gray-800">
                    Message Details
                  </Typography>
                  <Button
                    onClick={() => {
                      setShowMessageModal(false)
                      setSelectedMessage(null)
                    }}
                    className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  >
                    ×
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Typography variant="h5" className="text-gray-800 font-semibold text-sm font-medium">
                        Name
                      </Typography>
                      <Typography variant="caption" className="text-gray-800">
                        {contactMessageDetails.fullName ||
                          `${contactMessageDetails.firstName || ''} ${contactMessageDetails.lastName || ''}`.trim() ||
                          'Anonymous'}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="h5" className="text-gray-800 font-semibold text-sm font-medium">
                        Status
                      </Typography>
                      <div
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          contactMessageDetails.status
                        )}`}
                      >
                        {getStatusIcon(contactMessageDetails.status)}
                        <span className="capitalize">{contactMessageDetails.status?.replace('_', ' ')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Typography variant="h5" className="text-gray-800 font-semibold text-sm ">
                        Email
                      </Typography>
                      <Typography variant="caption" className="text-gray-800">
                        {contactMessageDetails.email || '-'}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="h5" className="text-gray-800 font-semibold text-sm ">
                        Category
                      </Typography>
                      <Typography variant="caption" className="text-gray-800">
                        {contactMessageDetails.category || '-'}
                      </Typography>
                    </div>
                  </div>

                  <div>
                    <Typography variant="h5" className="text-gray-800 font-semibold text-sm ">
                      Source
                    </Typography>
                    <Typography variant="caption" className="text-gray-800">
                      {contactMessageDetails.source || '-'}
                    </Typography>
                  </div>

                  <div>
                    <Typography variant="h5" className="text-gray-800 font-semibold text-sm ">
                      Message
                    </Typography>
                    <Typography variant="body" className="text-gray-800 bg-gray-50 p-3 rounded">
                      {contactMessageDetails.description ||
                        contactMessageDetails.message ||
                        contactMessageDetails.body ||
                        '-'}
                    </Typography>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                    <div>
                      <Typography variant="h5" className="text-gray-800 font-semibold text-sm ">
                        Received:
                      </Typography>
                      <Typography variant="caption" className="text-sm ">
                        {new Date(contactMessageDetails.createdAt).toLocaleString()}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="h5" className="text-gray-800 font-semibold text-sm ">
                        Last Updated:
                      </Typography>
                      <Typography variant="caption" className="text-sm ">
                        {new Date(contactMessageDetails.updatedAt).toLocaleString()}
                      </Typography>
                    </div>
                  </div>

                  {/* Status Update Actions */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      onClick={() => handleStatusUpdate(selectedMessage, 'in_progress')}
                      className="px-4 py-2 bg-teal-500 text-md text-white hover:bg-teal-600 transition-colors rounded disabled:opacity-50"
                      disabled={contactMessageDetails.status === 'in_progress' || loading}
                    >
                      Mark In Progress
                    </Button>
                    <Button
                      onClick={() => handleStatusUpdate(selectedMessage, 'resolved')}
                      className="px-4 py-2 bg-teal-500 text-md text-white hover:bg-teal-600 transition-colors rounded disabled:opacity-50"
                      disabled={contactMessageDetails.status === 'resolved' || loading}
                    >
                      Mark Resolved
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  )
}

export default ContactUs
